/**
 * VEDANGJYOTISHAM V6.3 — VEDIC DESTINY DYNAMIC UPAYAS & PANCHANGA TIMING ENGINE
 * (DESTINY-UPAYA-01)
 *
 * Implements deterministic, chart-specific, dasha-aligned prescriptive remedies
 * grounded in canonical Panchanga timing (Vara, Tithi, Vrat, Sankranti, Pitru Paksha).
 *
 * Core Invariants:
 * 1. ZERO duplicate calculation engines: Consumes canonical VEDIC_UPAYAS,
 *    calculateVartamanaCurrentState, and calculateAuthoritativePanchanga.
 * 2. Dynamic Dasha Resolution: Grounded in focusDate rather than static birth dashas.
 * 3. Human Agency Preserved: Upayas are conscious energetic harmonizations, never fear-based.
 * 4. Full Bilingual Parity: Complete English and Hindi shastric citations and guidance.
 */

import { CompleteKundali, Language, PlanetName } from '../../types/kundali';
import { VEDIC_UPAYAS, VedicUpayaDetail } from '../gemstoneAdvisorEngine';
import { calculateAuthoritativePanchanga, PanchangaCoordinates } from '../panchangaEngine';
import { calculateVartamanaCurrentState } from '../vartamanaSthitiEngine';
import { formatDate } from '../dateUtils';

export interface DestinyPrescribedUpaya {
  category: 'MANTRA' | 'STOTRA' | 'GEMSTONE' | 'SEVA';
  titleEn: string;
  titleHi: string;
  targetPlanet: PlanetName;
  planetNameHi: string;
  roleEn: string;
  roleHi: string;
  primaryActionEn: string;
  primaryActionHi: string;
  prescribedText: string;
  timingGuidanceEn: string;
  timingGuidanceHi: string;
  shastricRuleEn: string;
  shastricRuleHi: string;
  panchangaAlignmentNoticeEn?: string;
  panchangaAlignmentNoticeHi?: string;
  isPanchangaAlignedToday: boolean;
}

export interface VedicDestinyUpayaResult {
  activeMahadashaLord: PlanetName;
  activeAntardashaLord: PlanetName;
  activePratyantardashaLord?: PlanetName;
  dashaPathSummary: string;
  focusDate: Date;
  focusDateFormatted: string;
  primaryUpayaPlanet: PlanetName;
  panchangaTimingContext: {
    varaNameEn: string;
    varaNameHi: string;
    tithiNameEn: string;
    tithiNameHi: string;
    isVaraAlignedWithUpaya: boolean;
    hasActiveVrat: boolean;
    vratNameEn?: string;
    vratNameHi?: string;
    isSankrantiActive: boolean;
    sankrantiNameEn?: string;
    sankrantiNameHi?: string;
    isPitruPakshaActive: boolean;
    dailyGuidanceEn: string;
    dailyGuidanceHi: string;
  };
  cards: {
    mantra: DestinyPrescribedUpaya;
    stotra: DestinyPrescribedUpaya;
    gemstone: DestinyPrescribedUpaya;
    seva: DestinyPrescribedUpaya;
  };
  lifestylePrescriptionsEn: string[];
  lifestylePrescriptionsHi: string[];
}

const PLANET_NAMES_HI: Record<PlanetName, string> = {
  Sun: 'सूर्य',
  Moon: 'चन्द्र',
  Mars: 'मंगल',
  Mercury: 'बुध',
  Jupiter: 'बृहस्पति (गुरु)',
  Venus: 'शुक्र',
  Saturn: 'शनि देव',
  Rahu: 'राहु',
  Ketu: 'केतु',
  Ascendant: 'लग्न'
};

const PLANET_WEEKDAYS: Record<PlanetName, number[]> = {
  Sun: [0],          // Sunday
  Moon: [1],         // Monday
  Mars: [2],         // Tuesday
  Mercury: [3],      // Wednesday
  Jupiter: [4],      // Thursday
  Venus: [5],        // Friday
  Saturn: [6],       // Saturday
  Rahu: [6],         // Saturday / Rahu Kaal
  Ketu: [2, 6],      // Tuesday & Saturday
  Ascendant: [0]
};

const PLANET_STOTRAS: Record<PlanetName, { nameEn: string; nameHi: string; ruleEn: string; ruleHi: string }> = {
  Sun: {
    nameEn: 'Aditya Hridaya Stotra (Valmiki Ramayana)',
    nameHi: 'आदित्य हृदय स्तोत्र पाठ (वाल्मीकि रामायण)',
    ruleEn: 'Recite post Surya Arghya to dissolve professional bottlenecks and activate executive vitality.',
    ruleHi: 'प्रातः सूर्य देव को तांबे के लोटे से अर्घ्य देकर आदित्य हृदय स्तोत्र का पाठ करें।'
  },
  Moon: {
    nameEn: 'Chandramouleshwara Shiva Stotram & Dwadasha Jyotirlinga Stotra',
    nameHi: 'चन्द्रमौलेश्वर शिव स्तोत्र अथवा द्वादश ज्योतिर्लिंग स्मरण',
    ruleEn: 'Chant in the evening twilight with a calm mind to dissolve emotional anxiety and fortify mental serenity.',
    ruleHi: 'सायंकाल मानसिक शांति एवं चंद्र कृपा हेतु शिव स्तुति अथवा द्वादश ज्योतिर्लिंग का पाठ करें।'
  },
  Mars: {
    nameEn: 'Rinmochan Mangal Stotra & Sri Hanuman Chalisa (7 Repetitions)',
    nameHi: 'ऋणमोचक मंगल स्तोत्र एवं श्री हनुमान चालीसा पाठ (७ बार)',
    ruleEn: 'Recite facing South or East to overcome debts, property disputes, and cultivate resolute courage.',
    ruleHi: 'ऋण मुक्ति, भूमि विवाद निवारण एवं आत्मबल संवर्धन हेतु मंगलवार को हनुमान चालीसा का पाठ करें।'
  },
  Mercury: {
    nameEn: 'Sri Vishnu Sahasranama Stotram & Budha Kavacham',
    nameHi: 'श्री विष्णु सहस्रनाम स्तोत्र पाठ अथवा बुध कवच',
    ruleEn: 'Recite in forenoon wearing green attire to sharpen commercial acumen, analytical intellect, and eloquence.',
    ruleHi: 'बुधवार प्रातः श्री विष्णु सहस्रनाम का पाठ करें; इससे व्यापारिक बुद्धि एवं वाणी में ओजस्विता आती है।'
  },
  Jupiter: {
    nameEn: 'Sri Brihaspati Stotram & Guru Ashtakam',
    nameHi: 'श्री बृहस्पति स्तोत्र एवं गुरु अष्टकम पाठ',
    ruleEn: 'Recite in morning post-bath to awaken higher wisdom, activate dharma, and attract divine prosperity.',
    ruleHi: 'गुरुवार प्रातः शुद्ध पीत वस्त्र धारण कर गुरु स्तोत्र का पाठ करें; ज्ञान एवं कोष में वृद्धि होगी।'
  },
  Venus: {
    nameEn: 'Sri Suktam (Rigvedic Khila) & Mahalakshmi Ashtakam',
    nameHi: 'श्री सूक्तम् (ऋग्वैदिक) एवं महालक्ष्मी अष्टकम पाठ',
    ruleEn: 'Recite with pure cow ghee lamp on Friday mornings to attract grace, marital harmony, and aesthetic fulfillment.',
    ruleHi: 'शुक्रवार प्रातः गाय के घी का दीपक प्रज्वलित कर श्री सूक्तम् का पाठ करें; दांपत्य व ऐश्वर्य सुदृढ़ होगा।'
  },
  Saturn: {
    nameEn: 'Dasharatha Krit Shani Stotram & Hanuman Vadvanal Stotra',
    nameHi: 'दशरथ कृत शनि स्तोत्र एवं श्री हनुमान वड़वानल स्तोत्र पाठ',
    ruleEn: 'Recite under a Peepal tree or post sunset on Saturdays to dissolve karmic delays and cultivate structural discipline.',
    ruleHi: 'शनिवार सूर्यास्त के समय दशरथ कृत शनि स्तोत्र का पाठ करें; कर्म बाधाओं का शमन होता है।'
  },
  Rahu: {
    nameEn: 'Durga Saptashati (Argala & Keelaka Stotra) & Rahu Kavacham',
    nameHi: 'श्री दुर्गा सप्तशती (अर्गला व कीलक) एवं राहु कवच पाठ',
    ruleEn: 'Recite during Rahu Kaal or on Saturday evenings to clear deceptive illusions, anxieties, and hidden hurdles.',
    ruleHi: 'राहु काल अथवा शनिवार संध्या को दुर्गा सप्तशती का पाठ करें; आकस्मिक संकट एवं भ्रम दूर होते हैं।'
  },
  Ketu: {
    nameEn: 'Sri Ganesha Atharvashirsha & Ketu Pancharatna Stotram',
    nameHi: 'श्री गणपति अथर्वशीर्ष (११ पाठ) एवं केतु पंचरत्न स्तोत्र',
    ruleEn: 'Recite offering 21 Durva grass blades to Lord Ganesha to liberate spiritual intuition and dissolve enigmatic obstacles.',
    ruleHi: 'भगवान गणेश को दुर्वा अर्पित कर गणपति अथर्वशीर्ष का पाठ करें; गूढ़ अनुसंधान व मोक्ष मार्ग प्रशस्त होता है।'
  },
  Ascendant: {
    nameEn: 'Gayatri Mantra & Aditya Hridaya Stotram',
    nameHi: 'गायत्री मंत्र एवं आदित्य हृदय स्तोत्र',
    ruleEn: 'Recite during morning twilight for holistic physical vitality and life direction.',
    ruleHi: 'प्रातः काल देह आरोग्य एवं आत्मबल संवर्धन हेतु गायत्री मंत्र का जप करें।'
  }
};

const PLANET_GEMSTONES: Record<PlanetName, {
  gemEn: string;
  gemHi: string;
  fingerEn: string;
  fingerHi: string;
  metalEn: string;
  metalHi: string;
  rudrakshaEn: string;
  rudrakshaHi: string;
}> = {
  Sun: {
    gemEn: 'Ruby (Manikya)',
    gemHi: 'माणिक्य (Ruby)',
    fingerEn: 'Ring Finger',
    fingerHi: 'अनामिका अंगुली',
    metalEn: 'Gold or Copper',
    metalHi: 'स्वर्ण अथवा तांबा',
    rudrakshaEn: '1 Mukhi or 12 Mukhi Rudraksha',
    rudrakshaHi: '१ मुखी अथवा १२ मुखी रुद्राक्ष'
  },
  Moon: {
    gemEn: 'Pearl (Moti) or Moonstone',
    gemHi: 'मोती (Pearl) अथवा चंद्रकांत मणि',
    fingerEn: 'Little Finger',
    fingerHi: 'कनिष्ठिका अंगुली',
    metalEn: 'Pure Silver',
    metalHi: 'शुद्ध चांदी',
    rudrakshaEn: '2 Mukhi Rudraksha (Ardhanarishwara)',
    rudrakshaHi: '२ मुखी रुद्राक्ष (अर्धनारीश्वर)'
  },
  Mars: {
    gemEn: 'Red Coral (Moonga)',
    gemHi: 'लाल मूंगा (Red Coral)',
    fingerEn: 'Ring Finger',
    fingerHi: 'अनामिका अंगुली',
    metalEn: 'Copper or Gold',
    metalHi: 'तांबा अथवा स्वर्ण',
    rudrakshaEn: '3 Mukhi Rudraksha (Agni Rupa)',
    rudrakshaHi: '३ मुखी रुद्राक्ष (अग्नि स्वरूप)'
  },
  Mercury: {
    gemEn: 'Emerald (Panna)',
    gemHi: 'पन्ना (Emerald)',
    fingerEn: 'Little Finger',
    fingerHi: 'कनिष्ठिका अंगुली',
    metalEn: 'Gold or Bronze',
    metalHi: 'स्वर्ण अथवा कांस्य',
    rudrakshaEn: '4 Mukhi or 10 Mukhi Rudraksha',
    rudrakshaHi: '४ मुखी अथवा १० मुखी रुद्राक्ष'
  },
  Jupiter: {
    gemEn: 'Yellow Sapphire (Pukhraj)',
    gemHi: 'पीला पुखराज (Yellow Sapphire)',
    fingerEn: 'Index Finger',
    fingerHi: 'तर्जनी अंगुली',
    metalEn: '18k Gold or Brass',
    metalHi: 'स्वर्ण अथवा पीतल',
    rudrakshaEn: '5 Mukhi Rudraksha (Kalaagni Rudra)',
    rudrakshaHi: '५ मुखी रुद्राक्ष'
  },
  Venus: {
    gemEn: 'Diamond (Heera) or White Zircon',
    gemHi: 'हीरा अथवा श्वेत जरकन',
    fingerEn: 'Middle or Little Finger',
    fingerHi: 'मध्यमा अथवा कनिष्ठिका',
    metalEn: 'Silver or Platinum',
    metalHi: 'चांदी अथवा प्लैटिनम',
    rudrakshaEn: '6 Mukhi or 13 Mukhi Rudraksha',
    rudrakshaHi: '६ मुखी अथवा १३ मुखी रुद्राक्ष'
  },
  Saturn: {
    gemEn: 'Blue Sapphire (Neelam) or Amethyst (Kathela)',
    gemHi: 'नीलम अथवा जमुनिया/कटहला (Amethyst)',
    fingerEn: 'Middle Finger',
    fingerHi: 'मध्यमा अंगुली',
    metalEn: 'Panchadhatu or Iron',
    metalHi: 'पंचधातु अथवा लोहा',
    rudrakshaEn: '7 Mukhi or 14 Mukhi Rudraksha',
    rudrakshaHi: '७ मुखी अथवा १४ मुखी रुद्राक्ष'
  },
  Rahu: {
    gemEn: 'Hessonite (Gomed)',
    gemHi: 'गोमेद (Hessonite)',
    fingerEn: 'Middle Finger',
    fingerHi: 'मध्यमा अंगुली',
    metalEn: 'Ashtadhatu or Silver',
    metalHi: 'अष्टधातु अथवा चांदी',
    rudrakshaEn: '8 Mukhi Rudraksha (Ganesha Rupa)',
    rudrakshaHi: '८ मुखी रुद्राक्ष'
  },
  Ketu: {
    gemEn: "Cat's Eye (Lehsuniya)",
    gemHi: 'लहसुनिया (Cat\'s Eye)',
    fingerEn: 'Little or Middle Finger',
    fingerHi: 'कनिष्ठिका अथवा मध्यमा',
    metalEn: 'Panchadhatu or Silver',
    metalHi: 'पंचधातु अथवा चांदी',
    rudrakshaEn: '9 Mukhi Rudraksha (Navadurga Rupa)',
    rudrakshaHi: '९ मुखी रुद्राक्ष'
  },
  Ascendant: {
    gemEn: 'Lagna Lord Gemstone',
    gemHi: 'लग्न स्वामी का रत्न',
    fingerEn: 'Favorable Finger',
    fingerHi: 'शुभ अंगुली',
    metalEn: 'Auspicious Metal',
    metalHi: 'शुभ धातु',
    rudrakshaEn: '1 Mukhi or 5 Mukhi Rudraksha',
    rudrakshaHi: '१ मुखी अथवा ५ मुखी रुद्राक्ष'
  }
};

/**
 * Deterministically resolves dynamic prescriptive Upayas grounded in active Dasha
 * and canonical Panchanga cosmic timing.
 */
export function calculateVedicDestinyUpayas(
  kundali: CompleteKundali,
  focusDate: Date | string = new Date(),
  language: Language = 'en'
): VedicDestinyUpayaResult {
  const focusObj = new Date(focusDate);
  const isHi = language === 'hi';

  // 1. Resolve Active Dasha State at Focus Date
  let mdPlanet: PlanetName = 'Jupiter';
  let adPlanet: PlanetName = 'Moon';
  let pdPlanet: PlanetName | undefined;
  let dashaSummary = 'Jupiter – Moon';

  try {
    const vartamanaState = calculateVartamanaCurrentState(kundali, focusObj, language);
    if (vartamanaState?.dasha5Level) {
      mdPlanet = vartamanaState.dasha5Level.md.planet as PlanetName;
      adPlanet = vartamanaState.dasha5Level.ad.planet as PlanetName;
      pdPlanet = vartamanaState.dasha5Level.pd?.planet as PlanetName;
      dashaSummary = `${mdPlanet} – ${adPlanet}`;
    }
  } catch {
    // Fallback to kundali.dashas
    const activeDasha = kundali.dashas?.find(d => d.isCurrent) || kundali.dashas?.[0];
    if (activeDasha) {
      mdPlanet = (activeDasha.planet as PlanetName) || 'Jupiter';
      const activeAntar = activeDasha.antardashas?.find(a => a.isCurrent) || activeDasha.antardashas?.[0];
      if (activeAntar) {
        adPlanet = (activeAntar.planet as PlanetName) || 'Moon';
      }
      dashaSummary = `${mdPlanet} – ${adPlanet}`;
    }
  }

  // 2. Primary Upaya Planet (prioritize Antardasha lord as acute catalyst, with Mahadasha as anchor)
  const primaryUpayaPlanet: PlanetName = adPlanet || mdPlanet;

  // 3. Resolve Authoritative Panchanga at Focus Date
  const coords: PanchangaCoordinates = {
    latitude: kundali.birthDetails?.latitude || 28.6139,
    longitude: kundali.birthDetails?.longitude || 77.2090,
    elevationMeters: 0,
    timezoneOffsetMinutes: kundali.birthDetails?.timezoneOffsetMinutes ?? 330,
    placeName: kundali.birthDetails?.placeName || 'Indore, MP'
  };

  const authoritativePanchanga = calculateAuthoritativePanchanga(focusObj, coords);

  const dayOfWeek = focusObj.getDay();
  const alignedDays = PLANET_WEEKDAYS[primaryUpayaPlanet] || [0];
  const isVaraAligned = alignedDays.includes(dayOfWeek);

  const hasVrat = Boolean(authoritativePanchanga.vratObservance?.hasVrat);
  const vratNameEn = authoritativePanchanga.vratObservance?.vratName;
  const vratNameHi = authoritativePanchanga.vratObservance?.vratNameHi;

  const isSankrantiActive = Boolean(authoritativePanchanga.sankranti?.isSankrantiDay);
  const sankrantiNameEn = authoritativePanchanga.sankranti?.sankrantiNameEn;
  const sankrantiNameHi = authoritativePanchanga.sankranti?.sankrantiNameHi;

  const isPitruPakshaActive = Boolean(authoritativePanchanga.pitruPaksha?.isActive);

  // Daily Panchanga Guidance
  const tithiNameHi = `${authoritativePanchanga.tithi.paksha === 'Shukla Paksha' ? 'शुक्ल' : 'कृष्ण'} ${authoritativePanchanga.tithi.rawName}`;
  let dailyGuidanceEn = `Today is ${authoritativePanchanga.vara.nameEn} (${authoritativePanchanga.tithi.name} Tithi).`;
  let dailyGuidanceHi = `आज ${authoritativePanchanga.vara.nameSanskrit} (${tithiNameHi} तिथि) है।`;

  if (isVaraAligned) {
    dailyGuidanceEn += ` High potency day: Weekday strictly corresponds to active Antardasha lord ${primaryUpayaPlanet}.`;
    dailyGuidanceHi += ` विशेष फलदायी दिन: आज का वार सीधे सक्रिय अंतर्दशा स्वामी ${PLANET_NAMES_HI[primaryUpayaPlanet]} से संबंधित है।`;
  }

  if (hasVrat && vratNameEn) {
    dailyGuidanceEn += ` Sacred observance active: ${vratNameEn}. Enhance Upaya with satvik fasting.`;
    dailyGuidanceHi += ` पावन व्रत सक्रिय: ${vratNameHi || vratNameEn}। सात्विक उपवास से साधना को बल दें।`;
  }

  if (isSankrantiActive && sankrantiNameEn) {
    dailyGuidanceEn += ` Surya Sankranti active: ${sankrantiNameEn}. Punya Kaal offers amplified merit for Daana and Mantras.`;
    dailyGuidanceHi += ` सूर्य संक्रांति सक्रिय: ${sankrantiNameHi || sankrantiNameEn}। पुण्य काल में दान व जप का अनंत गुना फल मिलता है।`;
  }

  if (isPitruPakshaActive) {
    dailyGuidanceEn += ` Pitru Paksha active: Integrate Tarpan and Daana during Kutupa Muhurta.`;
    dailyGuidanceHi += ` पितृ पक्ष सक्रिय: कुतुप काल में तर्पण एवं अन्न-वस्त्र दान का संकल्प लें।`;
  }

  // 4. Retrieve Classical Planetary Upaya Details from VEDIC_UPAYAS
  const rawUpaya: VedicUpayaDetail = VEDIC_UPAYAS[primaryUpayaPlanet] || VEDIC_UPAYAS.Jupiter!;
  const stotraInfo = PLANET_STOTRAS[primaryUpayaPlanet] || PLANET_STOTRAS.Jupiter;
  const gemInfo = PLANET_GEMSTONES[primaryUpayaPlanet] || PLANET_GEMSTONES.Jupiter;

  // 5. Construct 4 Prescriptive Cards
  const panchangaNoticeEn = isVaraAligned
    ? `★ Today is ${authoritativePanchanga.vara.nameEn} — ideal day to commence or amplify this practice.`
    : undefined;
  const panchangaNoticeHi = isVaraAligned
    ? `★ आज ${authoritativePanchanga.vara.nameSanskrit} है — इस साधना के प्रारंभ अथवा तीव्रता हेतु सर्वोत्तम दिन।`
    : undefined;

  // Card 1: Mantra Sadhana
  const mantraCard: DestinyPrescribedUpaya = {
    category: 'MANTRA',
    titleEn: 'Planetary Beej Mantra Sadhana',
    titleHi: 'ग्रह बीज मंत्र साधना',
    targetPlanet: primaryUpayaPlanet,
    planetNameHi: PLANET_NAMES_HI[primaryUpayaPlanet],
    roleEn: `Active Antardasha Lord (${primaryUpayaPlanet})`,
    roleHi: `सक्रिय अंतर्दशा स्वामी (${PLANET_NAMES_HI[primaryUpayaPlanet]})`,
    primaryActionEn: `108 chants daily (${rawUpaya.mala})`,
    primaryActionHi: `१०८ नित्य जप (${rawUpaya.mala})`,
    prescribedText: rawUpaya.beejaMantra,
    timingGuidanceEn: `${rawUpaya.japCount}. Preferred time: ${rawUpaya.daana.time}.`,
    timingGuidanceHi: `${rawUpaya.japCount}। शुभ समय: ${rawUpaya.daana.time}।`,
    shastricRuleEn: `Chant facing East during auspicious hora to balance ${primaryUpayaPlanet} energy.`,
    shastricRuleHi: `पूर्वाभिमुख होकर शुभ होरा में जप करें; ${PLANET_NAMES_HI[primaryUpayaPlanet]} के अनुकूल परिणाम प्राप्त होंगे।`,
    panchangaAlignmentNoticeEn: panchangaNoticeEn,
    panchangaAlignmentNoticeHi: panchangaNoticeHi,
    isPanchangaAlignedToday: isVaraAligned
  };

  // Card 2: Sacred Stotra
  const stotraCard: DestinyPrescribedUpaya = {
    category: 'STOTRA',
    titleEn: 'Sacred Shastric Stotra Recitation',
    titleHi: 'दैनिक स्तोत्र पाठ',
    targetPlanet: primaryUpayaPlanet,
    planetNameHi: PLANET_NAMES_HI[primaryUpayaPlanet],
    roleEn: `Harmonizing ${primaryUpayaPlanet} and Mahadasha Lord ${mdPlanet}`,
    roleHi: `${PLANET_NAMES_HI[primaryUpayaPlanet]} एवं महादशा स्वामी ${PLANET_NAMES_HI[mdPlanet]} का समन्व्य`,
    primaryActionEn: 'Daily Recitation at Sunrise or Twilight',
    primaryActionHi: 'प्रातः अथवा संध्या काल में नित्य पाठ',
    prescribedText: isHi ? stotraInfo.nameHi : stotraInfo.nameEn,
    timingGuidanceEn: `Deity: ${rawUpaya.deity}. Best performed on ${rawUpaya.vrata.day}.`,
    timingGuidanceHi: `आराध्य देव: ${rawUpaya.deity}। उत्तम दिवस: ${rawUpaya.vrata.day}।`,
    shastricRuleEn: stotraInfo.ruleEn,
    shastricRuleHi: stotraInfo.ruleHi,
    panchangaAlignmentNoticeEn: panchangaNoticeEn,
    panchangaAlignmentNoticeHi: panchangaNoticeHi,
    isPanchangaAlignedToday: isVaraAligned
  };

  // Card 3: Gemstone & Rudraksha
  const gemstoneCard: DestinyPrescribedUpaya = {
    category: 'GEMSTONE',
    titleEn: 'Fortifying Gemstone & Rudraksha Alignment',
    titleHi: 'अनुकूल रत्न एवं रुद्राक्ष परामर्श',
    targetPlanet: primaryUpayaPlanet,
    planetNameHi: PLANET_NAMES_HI[primaryUpayaPlanet],
    roleEn: 'Fortifying / Calibrating',
    roleHi: 'ऊर्जा संवर्धन एवं संतुलन',
    primaryActionEn: `${gemInfo.fingerEn} / ${gemInfo.metalEn}`,
    primaryActionHi: `${gemInfo.fingerHi} / ${gemInfo.metalHi}`,
    prescribedText: isHi ? gemInfo.gemHi : gemInfo.gemEn,
    timingGuidanceEn: `Alternative: ${gemInfo.rudrakshaEn}. Wear during ${rawUpaya.daana.day} auspicious hora.`,
    timingGuidanceHi: `रुद्राक्ष विकल्प: ${gemInfo.rudrakshaHi}। ${rawUpaya.daana.day} शुभ होरा में धारण करें।`,
    shastricRuleEn: rawUpaya.rudraksha.benefits,
    shastricRuleHi: `यह उपाय ${PLANET_NAMES_HI[primaryUpayaPlanet]} की नकारात्मकता का शमन कर जीवन में स्थिरता प्रदान करता है।`,
    panchangaAlignmentNoticeEn: panchangaNoticeEn,
    panchangaAlignmentNoticeHi: panchangaNoticeHi,
    isPanchangaAlignedToday: isVaraAligned
  };

  // Card 4: Seva & Daana
  const daanaItemsEn = rawUpaya.daana.items.slice(0, 3).join(', ');
  const daanaItemsHi = rawUpaya.daana.items.slice(0, 3).join(', ');

  const sevaCard: DestinyPrescribedUpaya = {
    category: 'SEVA',
    titleEn: 'Seva & Daana Protocol',
    titleHi: 'सेवा एवं दान संकल्प',
    targetPlanet: primaryUpayaPlanet,
    planetNameHi: PLANET_NAMES_HI[primaryUpayaPlanet],
    roleEn: 'Karmic Remediation (Sattvic Charity)',
    roleHi: 'कर्म शोधन (सात्विक दान)',
    primaryActionEn: `${rawUpaya.daana.day} (${rawUpaya.daana.time})`,
    primaryActionHi: `${rawUpaya.daana.day} (${rawUpaya.daana.time})`,
    prescribedText: isHi ? daanaItemsHi : daanaItemsEn,
    timingGuidanceEn: `Recipient: ${rawUpaya.daana.recipient}.`,
    timingGuidanceHi: `सुपात्र: ${rawUpaya.daana.recipient}।`,
    shastricRuleEn: rawUpaya.lifestyle[0] || 'Engage in selfless service with humility.',
    shastricRuleHi: rawUpaya.lifestyle[0] || 'निःस्वार्थ भाव से दीन-दुखियों की सेवा करें।',
    panchangaAlignmentNoticeEn: panchangaNoticeEn,
    panchangaAlignmentNoticeHi: panchangaNoticeHi,
    isPanchangaAlignedToday: isVaraAligned
  };

  return {
    activeMahadashaLord: mdPlanet,
    activeAntardashaLord: adPlanet,
    activePratyantardashaLord: pdPlanet,
    dashaPathSummary: dashaSummary,
    focusDate: focusObj,
    focusDateFormatted: formatDate(focusObj),
    primaryUpayaPlanet,
    panchangaTimingContext: {
      varaNameEn: authoritativePanchanga.vara.nameEn,
      varaNameHi: authoritativePanchanga.vara.nameSanskrit,
      tithiNameEn: authoritativePanchanga.tithi.name,
      tithiNameHi,
      isVaraAlignedWithUpaya: isVaraAligned,
      hasActiveVrat: hasVrat,
      vratNameEn,
      vratNameHi,
      isSankrantiActive,
      sankrantiNameEn,
      sankrantiNameHi,
      isPitruPakshaActive,
      dailyGuidanceEn,
      dailyGuidanceHi
    },
    cards: {
      mantra: mantraCard,
      stotra: stotraCard,
      gemstone: gemstoneCard,
      seva: sevaCard
    },
    lifestylePrescriptionsEn: rawUpaya.lifestyle,
    lifestylePrescriptionsHi: rawUpaya.lifestyle
  };
}
